import React, { Component } from 'react';
import { Modal } from 'react-bootstrap';

import { computePopupWidth, MIN_POPUP_WIDTH } from './popupWidth.js';

import './FileViewerPopup.css';

//width helpers live in popupWidth.js (shared with the yaml editor); re-exported so the
//existing tests and consumers keep one import point
export { widestLineChars, computePopupWidth, MIN_POPUP_WIDTH, WIDTH_ALLOWANCE, FALLBACK_CHAR_WIDTH } from './popupWidth.js';

//Read-only text viewer popup for a recording's plain-text files (logs, timestamps, launch
//files, ...). show(recording, filePath) loads the file into a read-only textbox. The only
//control is the close icon in the top right corner -- deliberately no save or other buttons.
//Loads via the static maps/ path (browser cache bypassed): nothing edits these files in-app,
//so the yaml editor's save-then-reopen staleness problem does not apply here.
class FileViewerPopup extends Component {

    constructor(props) {
        super(props);

        this.state = {
            showPopup: false,
            filePath: '',
            content: '',
            loading: false,
            status: null, //null | 'ok' | 'bad' -- tick/cross marker in the title bar
            popupWidth: MIN_POPUP_WIDTH
        };

        this.titleBarRef = React.createRef();

        //invalidates in-flight loads: populate() only applies when its token is still current,
        //so a slow service response cannot reopen a closed popup or overwrite a newer one
        this.loadGeneration = 0;
    }

    //react-bootstrap's Modal owns the .modal-dialog element and does not forward a style
    //prop to it, so the computed width is applied imperatively after each open/update by
    //walking up from an element we do own. The css caps it at the screen width.
    componentDidUpdate() {

        if (this.state.showPopup && this.titleBarRef.current)
        {
            const dialog = this.titleBarRef.current.closest('.modal-dialog');

            if (dialog)
            {
                dialog.style.width = this.state.popupWidth + 'px';
            }
        }
    }

    show = (recording, filePath) => {

        fetch('maps/' + recording + '/' + filePath, { cache: 'no-store' })
        .then(res => res.text())
        .then((text) => {
            this.showText(filePath, text);
        })
        .catch(console.log)
    }

    //open the popup on already-prepared text (e.g. a parsed las header) with the given title;
    //status 'ok'/'bad' shows the green tick / red cross in the title bar's left corner
    showText = (title, text, status) => {
        this.loadGeneration++;

        this.setState({
            showPopup: true,
            filePath: title,
            content: text,
            loading: false,
            status: status || null,
            popupWidth: computePopupWidth(text)
        });
    }

    //open the popup immediately with an animated spinner while a service call is in flight;
    //returns a token to hand back to populate() once the text is ready
    showLoading = (title) => {
        this.loadGeneration++;

        this.setState({
            showPopup: true,
            filePath: title,
            content: '',
            loading: true,
            status: null,
            popupWidth: MIN_POPUP_WIDTH
        });

        return this.loadGeneration;
    }

    //swap the spinner for the loaded text -- ignored when the token is stale (the popup was
    //closed, or another load has started since)
    populate = (token, text) => {

        if (token !== this.loadGeneration || !this.state.loading)
        {
            return;
        }

        this.setState({
            content: text,
            loading: false,
            popupWidth: computePopupWidth(text)
        });
    }

    handleClose = () => {
        this.loadGeneration++; //any in-flight populate is now stale

        this.setState({
            showPopup: false,
            content: '',
            loading: false
        });
    }

    render() {
        return (
            <Modal
              className="fileViewerRoot"
              backdropClassName="fileViewerBackdrop"
              dialogClassName="fileViewerModal"
              show={this.state.showPopup}
              onHide={this.handleClose}
              backdrop="static"
              animation={false}
              centered
            >
            <Modal.Body className="fileViewerBody">
                <div className="fileViewerTitleBar" ref={this.titleBarRef}>
                    {'ok' === this.state.status &&
                        <span className="fileViewerStatus fileViewerStatusOk" aria-label="File size check passed">
                            <svg width="20" height="20" viewBox="0 0 16 16" aria-hidden="true">
                                <circle cx="8" cy="8" r="7.2" fill="#2E7D32" />
                                <path d="M4.4 8.2 L7 10.8 L11.6 5.6" fill="none" stroke="#FFFFFF" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </span>
                    }
                    {'bad' === this.state.status &&
                        <span className="fileViewerStatus fileViewerStatusBad" aria-label="File size check failed">
                            <svg width="20" height="20" viewBox="0 0 16 16" aria-hidden="true">
                                <circle cx="8" cy="8" r="7.2" fill="#C62828" />
                                <path d="M5.2 5.2 L10.8 10.8 M10.8 5.2 L5.2 10.8" fill="none" stroke="#FFFFFF" strokeWidth="1.8" strokeLinecap="round" />
                            </svg>
                        </span>
                    }
                    <span className="fileViewerTitle">{this.state.filePath}</span>
                    <button type="button" className="fileViewerClose" aria-label="Close viewer" onClick={this.handleClose}>
                        <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
                            <path d="M3 3 L13 13 M13 3 L3 13" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                    </button>
                </div>
                {this.state.loading ? (
                    <div className="fileViewerLoading">
                        <div className="fileViewerSpinner"></div>
                    </div>
                ) : (
                    <textarea
                        className="fileViewerTextarea"
                        value={this.state.content}
                        readOnly
                        spellCheck={false}
                        wrap="off"
                    />
                )}
            </Modal.Body>
            </Modal>
        )
    }
}

export default FileViewerPopup;
